import MainLayout from "../../components/layouts/MainLayout"
import React, { useEffect, useState } from "react";
import { EyeFilled, EditFilled, DeleteFilled, SearchOutlined, ReloadOutlined, PlusOutlined, SyncOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { Table, Input, Select, Button, Tag, Space, message, Popconfirm, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Auth } from "../../utils/Auth";
import { DateFormat } from "../../utils/formatDate";
import { DeviseApi } from "../../api/deviseApi";

const ListDevisesView = () => {
    const navigate = useNavigate()
    const abortController = new AbortController()

    const [devises, setDevises] = useState([])
    const [loading, setLoading] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(15)

    const [search, setSearch] = useState('')
    const [statut, setStatut] = useState(undefined)
    const [isoCode, setIsoCode] = useState('')

    const buildFilters = (currentPage, currentPerPage) => {
        const filters = { page: currentPage, per_page: currentPerPage }
        if (search) filters.search = search
        if (statut) filters.statut = statut
        if (isoCode) filters.iso_code = isoCode
        return filters
    }

    const handleFetchData = async (currentPage = page, currentPerPage = perPage) => {
        setLoading(true)
        try {
            const res = await DeviseApi.getDevises(buildFilters(currentPage, currentPerPage), abortController.signal)

            if (!res || !res.success) {
                setLoading(false)
                return
            }

            setDevises(res.data.data ?? [])
            setTotal(res.data.total ?? 0)
            setPage(res.data.current_page ?? currentPage)
            setPerPage(res.data.per_page ?? currentPerPage)
            setLoading(false)
        } catch (error) {
            console.log(error)
            setLoading(false)
            const messages = await error?.messages ?? error?.message

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken()
                        return navigate(`/login`)
                    }
                });
        }
    }

    const handleSearch = () => handleFetchData(1, perPage)

    const handleReset = () => {
        setSearch('')
        setStatut(undefined)
        setIsoCode('')
        setTimeout(() => handleFetchData(1, perPage), 0)
    }

    const handleTableChange = (pagination) => handleFetchData(pagination.current, pagination.pageSize)

    const handleDetail = (devise) => navigate(`/devises/${devise.id}/details`)
    const handleEdit = (devise) => navigate(`/devises/${devise.id}/modifier`)
    const handleAdd = () => navigate('/devises/ajouter')

    const handleActivate = async (devise) => {
        try {
            const res = await DeviseApi.activateDevise(devise.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de l'activation de la devise");
        }
    }

    const handleDeactivate = async (devise) => {
        try {
            const res = await DeviseApi.deactivateDevise(devise.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la désactivation de la devise");
        }
    }

    const handleSetDefault = async (devise) => {
        try {
            const res = await DeviseApi.setDefaultDevise(devise.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la définition de la devise par défaut");
        }
    }

    const handleDelete = async (devise) => {
        try {
            const res = await DeviseApi.deleteDevise(devise.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la suppression de la devise");
        }
    }

    const handleSyncRates = async () => {
        setSyncing(true)
        try {
            const res = await DeviseApi.syncRates(abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la synchronisation des taux");
        } finally {
            setSyncing(false)
        }
    }

    useEffect(() => {
        handleFetchData(1, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns = [
        { title: 'Nom', dataIndex: 'name', key: 'name' },
        { title: 'Code ISO', dataIndex: 'iso_code', key: 'iso_code' },
        { title: 'Symbole', dataIndex: 'symbol', key: 'symbol' },
        {
            title: 'Taux (vs référence)',
            dataIndex: 'exchange_rate',
            key: 'exchange_rate',
            render: (value) => Number(value).toFixed(6),
        },
        {
            title: 'Devise de référence',
            dataIndex: 'is_default',
            key: 'is_default',
            render: (value) => value ? <Tag color="gold">Oui</Tag> : <Tag>Non</Tag>,
        },
        {
            title: 'Statut',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (value) => value ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
        },
        {
            title: 'Dernière synchro.',
            dataIndex: 'last_synced_at',
            key: 'last_synced_at',
            render: (value) => value ? DateFormat.formatDate(value) : 'Jamais',
        },
        { title: 'Source', dataIndex: 'rate_source', key: 'rate_source' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, devise) => (
                <Space>
                    <EyeFilled className="text-green-600 cursor-pointer hover:text-green-800" onClick={() => handleDetail(devise)} />
                    <EditFilled className="text-blue-600 cursor-pointer hover:text-blue-800" onClick={() => handleEdit(devise)} />
                    {!devise.is_default && (
                        <Tooltip title="Définir comme devise par défaut">
                            <StarOutlined className="text-yellow-600 cursor-pointer hover:text-yellow-800" onClick={() => handleSetDefault(devise)} />
                        </Tooltip>
                    )}
                    {devise.is_default && <StarFilled className="text-yellow-600" />}
                    {devise.is_active ? (
                        !devise.is_default && (
                            <Tooltip title="Désactiver">
                                <Button size="small" onClick={() => handleDeactivate(devise)}>Désactiver</Button>
                            </Tooltip>
                        )
                    ) : (
                        <Tooltip title="Activer">
                            <Button size="small" type="primary" onClick={() => handleActivate(devise)}>Activer</Button>
                        </Tooltip>
                    )}
                    {!devise.is_default && (
                        <Popconfirm title="Supprimer cette devise ?" onConfirm={() => handleDelete(devise)} okText="Oui" cancelText="Non">
                            <DeleteFilled className="text-red-600 cursor-pointer hover:text-red-800" />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ]

    return <MainLayout>
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">Gestion des devises</h1>
                <Space>
                    <Button icon={<SyncOutlined spin={syncing} />} onClick={handleSyncRates} loading={syncing}>
                        Synchroniser les taux
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Ajouter une devise
                    </Button>
                </Space>
            </div>

            <div className="bg-white p-4 rounded shadow mb-4">
                <Space wrap>
                    <Input
                        placeholder="Rechercher (nom, code ISO)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 220 }}
                    />
                    <Input
                        placeholder="Code ISO exact"
                        value={isoCode}
                        onChange={(e) => setIsoCode(e.target.value)}
                        style={{ width: 160 }}
                    />
                    <Select
                        placeholder="Statut"
                        value={statut}
                        onChange={setStatut}
                        allowClear
                        style={{ width: 180 }}
                        options={[
                            { value: 'ACTIVE', label: 'Active' },
                            { value: 'INACTIVE', label: 'Inactive' },
                        ]}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Rechercher
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Réinitialiser
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={devises}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: perPage,
                    total: total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
        </div>
    </MainLayout>
}

export default ListDevisesView
